import { get, post, put, del } from './http';

export interface DynamicMenu {
  id?: number;
  key: string;
  label: string;
  icon: string;
  routePath: string;
  group: 'main' | 'content' | 'system' | 'admin';
  sortOrder: number;
  isVisible?: boolean;
  canEdit: boolean;
}

export interface AdminPermission {
  key: string;
  label: string;
  icon?: string;
  canView: boolean;
  canEdit: boolean;
}

export type AdminRole = 'super' | 'admin';

export interface AdminAccount {
  id: string;
  username?: string;
  name: string;
  email: string;
  role: AdminRole;
  status: 'active' | 'inactive';
  avatar: string;
  avatarUrl: string;
  createdAt: string;
  lastLogin: string;
  permissions: AdminPermission[];
}

export interface AdminMemberCandidate {
  id: number;
  name: string;
  email: string;
  phone: string | null;
  daohao: string | null;
  username: string | null;
}

export interface AdminAccountPayload {
  username: string;
  name: string;
  email: string;
  memberUserId?: number;
  role: AdminRole;
  permissions: {
    key: string;
    canView: boolean;
    canEdit: boolean;
  }[];
}

const DYNAMIC_MENUS_CACHE_TTL = 5000;
type DynamicMenusCacheKey = 'visible' | 'hidden';
interface DynamicMenusCacheEntry {
  data: DynamicMenu[] | null;
  cachedAt: number;
  inFlight: Promise<DynamicMenu[]> | null;
}

const dynamicMenusCache = new Map<DynamicMenusCacheKey, DynamicMenusCacheEntry>();

function getDynamicMenusCacheKey(includeHidden: boolean): DynamicMenusCacheKey {
  return includeHidden ? 'hidden' : 'visible';
}

function getDynamicMenusCacheEntry(key: DynamicMenusCacheKey): DynamicMenusCacheEntry {
  const existing = dynamicMenusCache.get(key);
  if (existing) return existing;
  const entry: DynamicMenusCacheEntry = {
    data: null,
    cachedAt: 0,
    inFlight: null,
  };
  dynamicMenusCache.set(key, entry);
  return entry;
}

export function invalidateDynamicMenusCache() {
  dynamicMenusCache.clear();
}

// 1. 获取当前管理员授权查看的动态侧边栏菜单列表
export function getDynamicMenus(options?: { includeHidden?: boolean }) {
  const includeHidden = options?.includeHidden === true;
  const cacheKey = getDynamicMenusCacheKey(includeHidden);
  const cacheEntry = getDynamicMenusCacheEntry(cacheKey);
  const now = Date.now();

  if (cacheEntry.data && now - cacheEntry.cachedAt < DYNAMIC_MENUS_CACHE_TTL) {
    return Promise.resolve(cacheEntry.data);
  }

  if (cacheEntry.inFlight) {
    return cacheEntry.inFlight;
  }

  cacheEntry.inFlight = get<DynamicMenu[]>('/admin/menus', includeHidden ? { params: { includeHidden: 1 } } : undefined)
    .then((res) => {
      cacheEntry.data = res;
      cacheEntry.cachedAt = Date.now();
      return res;
    })
    .finally(() => {
      cacheEntry.inFlight = null;
    });

  return cacheEntry.inFlight;
}

// 2. 超级管理员拉取所有管理员账号与特权矩阵列表
export function getAdminAccounts() {
  return get<AdminAccount[]>('/admin/accounts');
}

// 3. 搜索可提权为管理员的会员
export function searchAdminMemberCandidates(keyword?: string) {
  return get<AdminMemberCandidate[]>('/admin/accounts/member-candidates', keyword ? { params: { keyword } } : undefined);
}

// 4. 超级管理员创建新管理员
export function createAdminAccount(payload: AdminAccountPayload) {
  return post<{ message: string }>('/admin/accounts', payload);
}

// 5. 超级管理员更新管理员信息与板块特权配置
export function updateAdminAccount(id: string, payload: AdminAccountPayload) {
  return put<{ message: string }>(`/admin/accounts/${id}`, payload);
}

// 6. 超级管理员启用或停用某管理员账号
export function toggleAdminStatus(id: string, status: 'active' | 'inactive') {
  return put<{ message: string }>(`/admin/accounts/${id}/status`, { status });
}

// 7. 超级管理员重置协作管理员二级安全密码
export function resetAdminPassword(id: string, data: { newPassword: string }) {
  return post<{ message: string }>(`/admin/accounts/${id}/reset-password`, data);
}

// 8. 剥离撤销某管理员职务并降级为普通会员
export function demoteAdminToMember(id: string) {
  return post<{ message: string }>(`/admin/accounts/${id}/demote`);
}

// 9. 创建新的系统菜单
export function createDynamicMenu(payload: {
  menu_key: string;
  title: string;
  icon: string;
  route_path: string;
  menu_group: 'main' | 'content' | 'system' | 'admin';
  sort_order: number;
  is_visible: boolean;
}) {
  return post<{ message: string }>('/admin/menus', payload);
}

// 10. 更新现有菜单配置项
export function updateDynamicMenu(id: number, payload: {
  menu_key: string;
  title: string;
  icon: string;
  route_path: string;
  menu_group: 'main' | 'content' | 'system' | 'admin';
  sort_order: number;
  is_visible: boolean;
}) {
  return put<{ message: string }>(`/admin/menus/${id}`, payload);
}

// 11. 安全物理注销已有菜单
export function deleteDynamicMenu(id: number) {
  return del<{ message: string }>(`/admin/menus/${id}`);
}
